import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSSelect } from '../../../shared/components/form-controls';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { createAddressZone } from '../../services/userService';
import { DEFAULT_VALUES } from '../../constants/addressZoneFormConstants';

const AddressZoneDialog = ({ open, onClose, ptsAddressID, existingZoneCodes = [], onSaved }) => {
  const dispatch = useDispatch();
  const zones = useSelector((state) => state.zones || []);
  const [submitting, setSubmitting] = useState(false);
  const methods = useForm({ mode: 'onChange', defaultValues: DEFAULT_VALUES });
  const { control, setValue } = methods;
  const { isDirty } = useFormState({ control });
  const [zoneAgency, selectedZoneCode] = useWatch({ control, name: ['ZoneAgency', 'Zone'] });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const zoneAgencyOptions = useMemo(() => {
    const agencies = [...new Set(zones.map((zone) => zone.AgencyID).filter(Boolean))];
    return agencies.map((AgencyID) => ({ Code: AgencyID, Description: AgencyID }));
  }, [zones]);

  const zonesByAgencyOptions = useMemo(() => {
    if (!zoneAgency) return [];

    return zones
      .filter((zone) => zone.AgencyID === zoneAgency)
      .filter((zone) => !existingZoneCodes.includes(zone.ZoneCode))
      .map((zone) => ({ Code: zone.ZoneCode, Description: zone.Description }));
  }, [zones, zoneAgency, existingZoneCodes]);

  const handleAgencyChange = useCallback(() => setValue('Zone', null), [setValue]);

  const onSubmit = useCallback(async () => {
    if (!selectedZoneCode) return;

    await executeApiAction({
      setLoading: setSubmitting,
      dispatch,
      action: () =>
        createAddressZone({
          ptsParentID: ptsAddressID,
          ParentType: 'Address',
          ZoneCode: selectedZoneCode,
          IsDeleted: 0,
        }),
      onSuccess: () => {
        dispatch(notify('Zone added successfully', 'success'));
        onSaved?.();
      },
    });
  }, [selectedZoneCode, dispatch, ptsAddressID, onSaved]);

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this zone. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <>
      <RMSCommonDialog
        open={open}
        title="Add Zone"
        onClose={requestClose}
        onSave={handleSave}
        loading={submitting}
        saveDisabled={submitting || !selectedZoneCode}
        maxWidth="sm"
        fullWidth
        closeOnBackdropClick={false}
        saveButton={{
          show: true,
          type: 'simple',
          label: 'Save',
          variant: 'contained',
          color: 'primary',
        }}
        cancelButton={{
          show: true,
          type: 'simple',
          label: 'Close',
          variant: 'outlined',
          color: 'secondary',
        }}>
        <FormProvider {...methods}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <RMSSelect
                name="ZoneAgency"
                label="Zone Agency"
                options={zoneAgencyOptions}
                optionValue="Code"
                optionLabel="Description"
                showLabelOnly
                onChange={handleAgencyChange}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSSelect
                name="Zone"
                label="Zone"
                options={zonesByAgencyOptions}
                optionValue="Code"
                optionLabel="Description"
                disabled={!zoneAgency}
              />
            </Grid>
          </Grid>
        </FormProvider>
      </RMSCommonDialog>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </>
  );
};

export default AddressZoneDialog;
